import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Instagram, Youtube, Music, Play, Eye, Clock, Radio, X } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import { recordRecentBroadcast } from '@/lib/recent';
import SafeImg from '@/components/SafeImg';

const PLATFORMS = [
  { label: 'All', value: 'all' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'Naver Shopping Live', value: 'naver' },
  { label: 'Kakao Shopping Live', value: 'kakao' },
  { label: 'Grip', value: 'grip' },
  { label: 'Clickmate', value: 'clickmate' }
];

const CATEGORIES = [
  { label: '패션', value: 'fashion' },
  { label: '뷰티', value: 'beauty' },
  { label: '푸드', value: 'food' },
  { label: '키즈', value: 'kids' },
  { label: '반려동물', value: 'pet' },
  { label: '전자제품', value: 'electronics' },
  { label: '홈', value: 'home' },
  { label: '스포츠', value: 'sports' },
  { label: '기타', value: 'others' }
];

const SORTS = [
  { label: '최신순', value: 'latest' },
  { label: '인기순', value: 'popular' },
  { label: '라이브중', value: 'live' },
  { label: '곧 시작', value: 'upcoming' }
];

const RECOMMENDED = [
  { label: '패션', slug: 'fashion' },
  { label: '뷰티', slug: 'beauty' },
  { label: '푸드', slug: 'food' },
  { label: '반려동물', slug: 'pet' }
];

const CATEGORY_LABEL = CATEGORIES.reduce((acc, c) => { acc[c.value] = c.label; return acc; }, {});

const getPlatformIcon = (platform) => {
  switch (platform) {
    case 'instagram': return <Instagram size={14} />;
    case 'youtube': return <Youtube size={14} />;
    case 'tiktok': return <Music size={14} />;
    default: return null;
  }
};

// Deterministic pseudo viewer count derived from schedule_id (stable across renders)
const computeViewers = (live) => {
  if (!live.schedule_id) return 0;
  let hash = 0;
  for (let i = 0; i < live.schedule_id.length; i++) {
    hash = ((hash << 5) - hash) + live.schedule_id.charCodeAt(i);
    hash |= 0;
  }
  const base = Math.abs(hash) % 3800 + 200;
  return live.status === 'live' ? base : Math.floor(base / 10);
};

const ResultCard = ({ live }) => {
  const isLive = live.status === 'live';
  const viewers = computeViewers(live);

  return (
    <div className="group bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-all duration-200" data-testid="result-card">
      <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
        <SafeImg
          src={live.thumbnail}
          kind="broadcast"
          alt={live.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {isLive && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-[#FF3B30] text-white text-xs font-bold rounded-full animate-pulse flex items-center gap-1" data-testid="result-live-badge">
            <Radio size={12} />
            LIVE
          </div>
        )}
        {!isLive && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-black/70 text-white text-xs font-medium rounded flex items-center gap-1">
            <Clock size={12} />
            {format(new Date(live.scheduled_at), 'M월 d일 HH:mm', { locale: ko })}
          </div>
        )}
        <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 text-white text-xs font-medium rounded-full flex items-center gap-1" data-testid="result-category-badge">
          {CATEGORY_LABEL[live.category] || live.category}
        </div>
      </div>

      <div className="p-4">
        <Link to={`/seller/${live.seller_id}`} className="flex items-center gap-2 mb-3 group/seller" data-testid="result-seller-link">
          <SafeImg
            src={live.seller_photo}
            kind="avatar"
            alt={live.seller_name}
            className="w-8 h-8 rounded-full object-cover"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover/seller:text-[#FF3B30] transition-colors">
            {live.seller_name}
          </span>
        </Link>

        <h3 className="font-bold text-base tracking-tight line-clamp-2 mb-3 min-h-[3rem]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="result-title">
          {live.title}
        </h3>

        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
          <span className="flex items-center gap-1 capitalize" data-testid="result-platform">
            {getPlatformIcon(live.platform)}
            <span>{live.platform}</span>
          </span>
          <span className="flex items-center gap-1" data-testid="result-viewers">
            <Eye size={12} />
            <span>{viewers.toLocaleString()}{isLive ? '' : ' 예정'}</span>
          </span>
        </div>

        {isLive ? (
          <a
            href={live.stream_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordRecentBroadcast(live)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold rounded-md transition-colors"
            data-testid="watch-live-button"
          >
            <Play size={14} fill="currentColor" />
            Watch Live
          </a>
        ) : (
          <Link
            to={`/seller/${live.seller_id}`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-bold rounded-md hover:bg-[#FF3B30] dark:hover:bg-[#FF3B30] dark:hover:text-white transition-colors"
            data-testid="view-seller-button"
          >
            판매자 보기
          </Link>
        )}
      </div>
    </div>
  );
};

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialQuery = params.get('q') || '';
  const initialCategory = params.get('category') || 'all';
  const initialPlatform = params.get('platform') || 'all';

  const [query, setQuery] = useState(initialQuery);
  const [inputValue, setInputValue] = useState(initialQuery);
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState(initialPlatform);
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState('latest');

  useEffect(() => {
    // Sync when URL query params change (e.g. user clicks a home category card)
    setQuery(initialQuery);
    setInputValue(initialQuery);
    setCategory(initialCategory);
    setPlatform(initialPlatform);
  }, [initialQuery, initialCategory, initialPlatform]);

  useEffect(() => {
    const fetchLives = async () => {
      setLoading(true);
      try {
        const res = await api.get('/lives/week');
        setLives(res.data || []);
      } catch (err) {
        console.error('Failed to fetch lives:', err);
        setLives([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLives();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = lives.filter((live) => {
      if (!q) return true;
      return (
        (live.title || '').toLowerCase().includes(q) ||
        (live.seller_name || '').toLowerCase().includes(q) ||
        (live.category || '').toLowerCase().includes(q) ||
        (live.platform || '').toLowerCase().includes(q)
      );
    });

    if (platform !== 'all') {
      out = out.filter((l) => l.platform === platform);
    }
    if (category !== 'all') {
      out = out.filter((l) => l.category === category);
    }

    switch (sort) {
      case 'popular':
        out = [...out].sort((a, b) => computeViewers(b) - computeViewers(a));
        break;
      case 'live':
        out = [...out].sort((a, b) => (b.status === 'live' ? 1 : 0) - (a.status === 'live' ? 1 : 0));
        break;
      case 'upcoming':
        out = [...out]
          .filter((l) => l.status !== 'ended')
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        break;
      case 'latest':
      default:
        out = [...out].sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
        break;
    }

    return out;
  }, [lives, query, platform, category, sort]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = inputValue.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const handleClear = () => {
    setInputValue('');
    navigate('/search');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="max-w-3xl mb-6" data-testid="search-form">
          <div className="relative group">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#FF3B30] transition-colors" size={22} />
            <input
              type="text"
              placeholder="판매자 또는 라이브 검색..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full pl-14 pr-32 py-4 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-full focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent text-base shadow-sm hover:shadow-md focus:shadow-lg transition-shadow"
              data-testid="search-input"
            />
            {inputValue && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-24 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#FF3B30] transition-colors"
                data-testid="search-clear"
                aria-label="Clear search"
              >
                <X size={18} />
              </button>
            )}
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2.5 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold rounded-full text-sm transition-colors"
              data-testid="search-submit"
            >
              검색
            </button>
          </div>
        </form>

        {/* Result Count */}
        <div className="mb-6 flex items-baseline gap-3">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="results-count">
            검색결과 <span className="text-[#FF3B30]">{filtered.length}</span>개
          </h1>
          {query && (
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-testid="search-query-display">
              "{query}"에 대한 결과
            </p>
          )}
        </div>

        {/* Platform Filters */}
        <div className="mb-4" data-testid="platform-filters">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 mb-2">Platform</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPlatform(p.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5 ${
                  platform === p.value
                    ? 'bg-[#FF3B30] text-white border-[#FF3B30] shadow-sm'
                    : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
                }`}
                data-testid={`platform-filter-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-4" data-testid="category-filters">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 mb-2">Category</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5 ${
                category === 'all'
                  ? 'bg-[#FF3B30] text-white border-[#FF3B30] shadow-sm'
                  : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
              }`}
              data-testid="category-filter-all"
            >
              전체
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCategory(c.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5 ${
                  category === c.value
                    ? 'bg-[#FF3B30] text-white border-[#FF3B30] shadow-sm'
                    : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'
                }`}
                data-testid={`category-filter-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div className="mb-8 flex items-center gap-3" data-testid="sort-filters">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Sort</p>
          <div className="flex flex-wrap gap-2">
            {SORTS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setSort(s.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  sort === s.value
                    ? 'text-[#FF3B30] underline underline-offset-4 decoration-2'
                    : 'text-gray-600 dark:text-gray-400 hover:text-[#FF3B30]'
                }`}
                data-testid={`sort-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results / Empty state */}
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="loading-state">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]"></div>
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="results-grid">
            {filtered.map((live) => (
              <ResultCard key={live.schedule_id} live={live} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center" data-testid="no-results">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF3B30]/10 mb-4">
              <Search size={28} className="text-[#FF3B30]" />
            </div>
            <h2 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              검색 결과가 없습니다.
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              다른 키워드로 다시 시도해보세요
            </p>

            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 mb-4">
              추천 카테고리
            </p>
            <div className="flex flex-wrap justify-center gap-3" data-testid="recommended-categories">
              {RECOMMENDED.map((r) => (
                <Link
                  key={r.slug}
                  to={`/search?q=${encodeURIComponent(r.slug)}`}
                  className="px-5 py-2.5 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                  data-testid={`recommended-${r.slug}`}
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
